/* eslint-disable react-hooks/set-state-in-effect, react/no-unescaped-entities */
/**
 * /ambassador/:citySlug — Per-city Ambassador Program landing page (iter-321).
 *
 * SEO goals: rich, unique local content per city (no thin pages), JobPosting +
 * FAQPage JSON-LD, internal links to the city's service pages + nearby cities.
 * Thin-flagged cities render fully but carry noindex (same convention as the
 * moving-services + careers city pages).
 */
import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link, Navigate } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import axios from 'axios';
import {
  ChevronLeft, ChevronRight, DollarSign, Users, TrendingUp, MapPin,
  CheckCircle, Link2, BarChart3, Wallet, Sparkles, UserPlus, Percent, Trophy,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { getCity, getNearbyCities, cityHref } from '../utils/cities';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const SITE_URL = 'https://book.vandeliveryapp.com';
const OG_IMAGE = `${SITE_URL}/images/og/ambassador-og.jpg`;

const hashOf = (s) => [...(s || '')].reduce((a, c) => a + c.charCodeAt(0), 0);
const pick = (arr, seed) => arr[seed % arr.length];

/* ── Localized copy builders (template rotation kills cross-city duplication) ── */
function buildIntro(city, nearby, fr = false) {
  const seed = hashOf(city.slug);
  const hoods = city.neighborhoods || [];
  const h1 = hoods[0] || city.region;
  const h2 = hoods[1] || city.region;
  const h3 = hoods[2] || city.region;
  const near = nearby[0]?.name || city.region;
  const cur = city.country_code === 'CA' ? 'CA$' : '$';

  if (fr) {
    const p1 = pick([
      `${city.name} est l'un des marchés de déménagement et de livraison les plus actifs desservis par VanDeliveryApp en ${city.region}. Chaque semaine, des ménages de ${h1} à ${h2} réservent des déménageurs, des livreurs, des équipes de débarras et des monteurs de meubles — et chacune de ces réservations peut mettre une commission dans la poche d'un ambassadeur.`,
      `De ${h1} à ${h2}, les résidents de ${city.name}, ${city.region} déménagent, rénovent et meublent leur logement toute l'année. Le programme Ambassadeur de VanDeliveryApp vous permet de transformer cette demande locale constante en revenu récurrent.`,
    ], seed);
    const p2 = pick([
      `Le modèle est simple : vous recevez un lien de parrainage unique que vous partagez à ${city.name} — chauffeurs cherchant un travail flexible, voisins qui déménagent, gestionnaires d'immeubles, agents immobiliers ou groupes communautaires autour de ${h3}. Chaque travail complété paie ${cur}5 lorsque le sous-total (avant taxes et frais) est de ${cur}100 ou plus, et ${cur}2 pour les plus petits travaux. Les commissions ne sont créditées que sur des réservations payées et confirmées.`,
      `Inscrivez-vous gratuitement, prenez votre lien personnel et partagez-le partout à ${city.name} — groupes étudiants, babillards de ${h3}, réseaux de propriétaires ou amis qui possèdent une fourgonnette. ${cur}10 de prime au premier travail d'un fournisseur parrainé, ${cur}5 par travail complété (${cur}2 sous ${cur}100 de sous-total) et 10 % des gains des ambassadeurs que vous recrutez.`,
    ], seed + 7);
    const p3 = pick([
      `Comme VanDeliveryApp dessert aussi ${near} et toute la région de ${city.region}, vos parrainages ne se limitent pas à un quartier. Un fournisseur parrainé à ${city.name} peut accepter des travaux dans toute la région — et vous continuez d'encaisser sur chacun d'eux, sans plafond, sans expiration et sans frais d'adhésion.`,
      `Votre rayon de gains dépasse ${city.name} : les fournisseurs parrainés acceptent des travaux partout en ${city.region}, y compris à ${near}, et chaque réservation payée compte. Un fournisseur actif faisant 20 travaux de ${cur}100+ par mois vous rapporte ${cur}100 par mois à lui seul.`,
    ], seed + 13);
    return [p1, p2, p3];
  }

  const p1 = pick([
    `${city.name} is one of the busiest moving and delivery markets VanDeliveryApp serves in ${city.region}. Every week, households across ${h1} and ${h2} book movers, delivery drivers, junk removal crews, and furniture assemblers through our marketplace — and every one of those bookings can put commission in an ambassador's pocket.`,
    `People in ${city.name}, ${city.region} move, renovate, declutter, and furnish their homes year-round — from ${h1} to ${h2}, demand for movers and delivery help never really stops. The VanDeliveryApp Ambassador Program lets you turn that constant local demand into a recurring income stream.`,
    `From apartment turnovers in ${h1} to family moves out toward ${h2}, ${city.name} generates thousands of moving, delivery, and labor jobs every year. As a VanDeliveryApp ambassador, you earn a commission each time someone you referred books and pays for a job — from home, on your own schedule, with no truck and no lifting.`,
  ], seed);

  const p2 = pick([
    `Here's the model: you get a unique referral link and share it with people in ${city.name} — drivers looking for flexible work, neighbors planning a move, property managers, real-estate agents, or local Facebook and community groups around ${h3}. Every completed job you referred pays ${cur}5 when the job subtotal (before taxes and booking fees) is ${cur}100 or more — ${cur}2 for smaller jobs. Every payout is triggered only after the booking is paid and confirmed, so earnings are tied to real completed work.`,
    `Getting started takes minutes: sign up free, grab your personal referral link, and share it around ${city.name} — student housing groups, ${h3} community boards, landlord networks, or friends who drive a van or pickup. Each completed job earns you ${cur}5 (${cur}2 when the subtotal is under ${cur}100), whether it came from a provider or a customer you referred. Commissions are only paid on bookings that are paid and confirmed — real jobs, real money.`,
    `You don't need to be a mover to earn from ${city.name}'s moving economy. Share your ambassador link with van and truck owners who want flexible work, or with anyone planning a move near ${h3}. When their bookings are paid and confirmed, you earn — ${cur}10 sign-up bonuses for new providers, ${cur}5 per completed job with a ${cur}100+ subtotal (${cur}2 below that), and 10% of the earnings generated by ambassadors you recruit.`,
  ], seed + 7);

  const p3 = pick([
    `Because VanDeliveryApp also serves ${near} and the wider ${city.region} area, your referrals aren't limited to one neighborhood. A provider you refer in ${city.name} can take jobs across the region — and you keep earning on every one of them, for as long as they stay active. There's no cap, no expiry, and no cost to join.`,
    `Your earning radius is bigger than ${city.name} itself: providers you refer can accept jobs across ${city.region}, including ${near}, and every paid booking counts toward your commissions. Referrals never expire — an active provider keeps generating income for you month after month.`,
    `Ambassadors in markets like ${city.name} do especially well because referrals compound: one active provider doing 20 qualifying jobs a month earns you ${cur}100 monthly on their own — refer five and that's ${cur}500 in recurring commissions before counting customer referrals or your ambassador team. And since VanDeliveryApp also operates in ${near}, your network can grow beyond city limits.`,
  ], seed + 13);

  return [p1, p2, p3];
}

function buildHoodTip(hood, city, i) {
  const tips = [
    `Share your link in ${hood} community groups and buy-nothing pages — moving help is one of the most-requested services.`,
    `Renters and new arrivals in ${hood} book movers and furniture delivery year-round. One post can earn for months.`,
    `Know a van or pickup owner near ${hood}? One referred provider can mean recurring monthly commission for you.`,
    `Property managers and realtors around ${hood} constantly need movers for clients — perfect referral partners.`,
    `${hood} sees steady apartment turnover — every move-in and move-out is a potential referral booking.`,
    `Local stores near ${hood} sell furniture and appliances daily; their customers need delivery — your link fills the gap.`,
  ];
  return tips[(hashOf(city.slug) + i) % tips.length];
}

function buildFaqs(city, fr = false) {
  const cur = city.country_code === 'CA' ? 'CA$' : '$';
  if (fr) {
    return [
      { q: `Qu'est-ce que le programme Ambassadeur VanDeliveryApp à ${city.name}?`,
        a: `C'est un programme de parrainage gratuit qui vous paie une commission pour faire croître la place de marché VanDeliveryApp à ${city.name}, ${city.region}. Vous partagez un lien personnel; lorsqu'un fournisseur ou un client parrainé complète une réservation payée et confirmée, vous gagnez — ${cur}10 au premier travail d'un fournisseur parrainé, ${cur}5 par travail complété dont le sous-total (avant taxes et frais) est de ${cur}100 ou plus (${cur}2 pour les plus petits travaux) et 10 % des gains des ambassadeurs que vous recrutez.` },
      { q: `Combien un ambassadeur est-il payé par travail à ${city.name}?`,
        a: `${cur}5 par travail complété lorsque le sous-total est de ${cur}100 ou plus — ce qui couvre la plupart des déménagements et livraisons multi-articles. Les travaux de moins de ${cur}100 de sous-total paient ${cur}2. Sans plafond et sans expiration, autant pour les fournisseurs que pour les clients parrainés.` },
      { q: `Comment et quand suis-je payé?`,
        a: `Les gains sont versés chaque mois par Stripe Connect directement dans votre compte bancaire. Les commissions ne sont créditées qu'une fois la réservation payée et confirmée.` },
      { q: `Ai-je besoin d'expérience, d'un véhicule ou d'un permis?`,
        a: `Non. Les ambassadeurs n'exécutent pas les travaux — vous faites la promotion de VanDeliveryApp et parrainez des gens. Aucuns frais, aucun horaire minimum. Il vous faut seulement un téléphone et des gens à qui partager votre lien à ${city.name}.` },
      { q: `Qui devrais-je parrainer à ${city.name}?`,
        a: `Trois groupes : (1) les propriétaires de fourgonnettes et camions qui veulent un travail flexible, (2) toute personne qui déménage ou a besoin de livraison, de débarras ou de montage de meubles, et (3) d'autres personnes motivées qui feraient de bons ambassadeurs — vous gagnez 10 % de leurs gains d'ambassadeur.` },
      { q: `Puis-je être ambassadeur et fournisseur de services en même temps?`,
        a: `Oui. Beaucoup de fournisseurs ont aussi un compte ambassadeur — ils parrainent les clients qu'ils ne peuvent pas servir et d'autres chauffeurs qu'ils connaissent.` },
    ];
  }
  return [
    { q: `What is the VanDeliveryApp Ambassador Program in ${city.name}?`,
      a: `It's a free referral program that pays you a commission for growing the VanDeliveryApp marketplace in ${city.name}, ${city.region}. You share a personal referral link; when a service provider or customer you referred completes a paid, confirmed booking, you earn — ${cur}10 when a referred provider finishes their first job, ${cur}5 for every completed job with a subtotal (before taxes and booking fees) of ${cur}100 or more (${cur}2 for smaller jobs), and 10% of the earnings of ambassadors you recruit.` },
    { q: `How much do ambassadors get paid per job in ${city.name}?`,
      a: `${cur}5 per completed job when the job's subtotal is ${cur}100 or more — which covers most moves, multi-item deliveries and junk removal jobs in ${city.name}. Jobs under ${cur}100 subtotal pay ${cur}2. This applies to jobs completed by providers you referred AND jobs booked by customers you referred — no cap, no expiry.` },
    { q: `How much can I earn as an ambassador in ${city.name}?`,
      a: `There's no cap. As a realistic example: 5 active referred providers averaging 20 qualifying jobs a month earn you ${cur}500/month, 10 referred customers booking twice a month add ${cur}100/month, sign-up bonuses add ${cur}50, and a small ambassador team can add ${cur}100+ — around ${cur}750+ per month in recurring income. Top ambassadors in busy markets like ${city.name} earn more.` },
    { q: 'How and when do I get paid?',
      a: 'Earnings are paid out monthly via Stripe Connect directly to your bank account. Commissions are only credited once a booking is paid and confirmed — so every dollar in your dashboard is backed by real completed work.' },
    { q: 'Do I need experience, a vehicle, or a licence?',
      a: `No. Ambassadors don't perform jobs — you promote VanDeliveryApp and refer people. There are no fees, no inventory, and no minimum hours. All you need is a phone and people to share your link with in ${city.name}.` },
    { q: `Who should I refer in ${city.name}?`,
      a: `Three groups: (1) van, pickup, and truck owners who want flexible paid work as service providers, (2) anyone planning a move or needing delivery, junk removal, or furniture assembly — friends, coworkers, community groups${(city.neighborhoods || [])[0] ? ` around ${city.neighborhoods[0]}` : ''}, and (3) other motivated people who'd make good ambassadors, since you earn 10% of your recruits' ambassador earnings.` },
    { q: 'Can I be an ambassador and a service provider at the same time?',
      a: 'Yes. Many providers also run an ambassador account — they refer customers they can\'t serve and other drivers they know, stacking referral income on top of their job earnings.' },
  ];
}

function buildJobPostingSchema(city) {
  const today = new Date();
  const inSixtyDays = new Date(today.getTime() + 60 * 24 * 3600 * 1000);
  const cc = (city.country_code || 'US').toUpperCase();
  return {
    '@context': 'https://schema.org',
    '@type': 'JobPosting',
    title: `Brand Ambassador — Referral Commissions — ${city.name}, ${city.region}`,
    description:
      `<p>VanDeliveryApp is looking for ambassadors in ${city.name}, ${city.region} to promote its moving, delivery and home-services marketplace and earn commission on every paid, confirmed booking they refer.</p>` +
      `<h3>What you'll do</h3><ul>` +
      `<li>Share your personal referral link with drivers, customers, and local communities in ${city.name}.</li>` +
      `<li>Earn $10 when a referred service provider completes their first job, plus $5 for every completed job with a subtotal of $100 or more ($2 for jobs under $100 subtotal).</li>` +
      `<li>Earn $5 for every $100+ subtotal job booked by customers you refer ($2 for smaller jobs) — no limit.</li>` +
      `<li>Recruit other ambassadors and earn 10% of their ambassador earnings.</li>` +
      `</ul><h3>Requirements</h3><ul>` +
      `<li>18+ with a smartphone and a bank account (payouts via Stripe Connect).</li>` +
      `<li>No fees, no experience, no vehicle required. Work whenever you want.</li>` +
      `</ul>`,
    datePosted: today.toISOString().slice(0, 10),
    validThrough: inSixtyDays.toISOString(),
    employmentType: 'CONTRACTOR',
    directApply: true,
    hiringOrganization: {
      '@type': 'Organization',
      name: 'VanDeliveryApp',
      sameAs: SITE_URL,
      logo: `${SITE_URL}/icons/icon-512x512.png`,
    },
    jobLocation: {
      '@type': 'Place',
      address: {
        '@type': 'PostalAddress',
        addressLocality: city.name,
        addressRegion: city.region_code || city.region,
        addressCountry: cc,
      },
    },
    baseSalary: {
      '@type': 'MonetaryAmount',
      currency: cc === 'CA' ? 'CAD' : 'USD',
      value: { '@type': 'QuantitativeValue', minValue: 100, maxValue: 1500, unitText: 'MONTH' },
    },
  };
}

export default function AmbassadorCityPage({ lang = 'en' }) {
  const { citySlug } = useParams();
  const navigate = useNavigate();
  const city = getCity(citySlug);
  const fr = lang === 'fr';

  // iter-366 — live local proof: top ambassadors leaderboard
  const [leaders, setLeaders] = useState([]);
  useEffect(() => {
    let live = true;
    axios.get(`${BACKEND_URL}/api/ambassador/gamification/leaderboard`)
      .then(r => { if (live) setLeaders(Array.isArray(r.data?.leaderboard) ? r.data.leaderboard : []); })
      .catch(() => {});
    return () => { live = false; };
  }, [citySlug]);

  if (!city) return <Navigate to="/ambassador" replace />;
  // French pages exist for Canadian cities only
  if (fr && (city.country_code || '').toUpperCase() !== 'CA') {
    return <Navigate to={`/ambassador/${city.slug}`} replace />;
  }

  const cur = city.country_code === 'CA' ? 'CA$' : '$';
  const nearby = getNearbyCities(citySlug, 10);
  const intro = buildIntro(city, nearby, fr);
  const faqs = buildFaqs(city, fr);
  const hoods = (city.neighborhoods || []).slice(0, 6);
  const isCA = (city.country_code || '').toUpperCase() === 'CA';

  const title = (fr
    ? `Devenez ambassadeur à ${city.name}, ${city.region_code} | VanDeliveryApp`
    : `Make Money From Home in ${city.name}, ${city.region_code} | VanDeliveryApp`).slice(0, 62);
  const metaDescription = (fr
    ? `Devenez ambassadeur VanDeliveryApp à ${city.name}, ${city.region}. ${cur}10 de prime, ${cur}5 par réservation complétée (${cur}2 sous ${cur}100) et 10 % de commissions d'équipe. Gratuit.`
    : `Become a VanDeliveryApp ambassador in ${city.name}, ${city.region}. Earn ${cur}10 provider sign-up bonuses, ${cur}5 per completed booking (${cur}2 under ${cur}100 subtotal) and 10% team commissions. Free to join.`).slice(0, 165);
  const canonical = fr ? `${SITE_URL}/fr/ambassadeur/${city.slug}` : `${SITE_URL}/ambassador/${city.slug}`;
  const enUrl = `${SITE_URL}/ambassador/${city.slug}`;
  const frUrl = `${SITE_URL}/fr/ambassadeur/${city.slug}`;

  const faqSchema = {
    '@context': 'https://schema.org', '@type': 'FAQPage',
    mainEntity: faqs.map(f => ({ '@type': 'Question', name: f.q, acceptedAnswer: { '@type': 'Answer', text: f.a } })),
  };
  const breadcrumbSchema = {
    '@context': 'https://schema.org', '@type': 'BreadcrumbList',
    itemListElement: [
      { '@type': 'ListItem', position: 1, name: 'Home', item: SITE_URL },
      { '@type': 'ListItem', position: 2, name: 'Ambassador Program', item: `${SITE_URL}/ambassador` },
      { '@type': 'ListItem', position: 3, name: city.name, item: canonical },
    ],
  };

  const registerHref = `/ambassador/register?city=${encodeURIComponent(city.slug)}&utm_source=ambassador_city&utm_campaign=${city.slug}`;

  const EARNING_CARDS = fr ? [
    { icon: UserPlus, color: 'blue', title: 'Parrainage de fournisseurs',
      lines: [[`${cur}10`, `Prime d'inscription quand un fournisseur parrainé complète son premier travail`], [`${cur}5`, `Par travail complété par votre fournisseur parrainé (sous-total ${cur}100+; ${cur}2 sous ${cur}100) — pour toujours`]] },
    { icon: Users, color: 'purple', title: 'Parrainage de clients',
      lines: [[`${cur}5`, `Par travail réservé par votre client parrainé (sous-total ${cur}100+; ${cur}2 sous ${cur}100)`], ['Sans limite', 'Plus vous parrainez de clients actifs, plus vous gagnez chaque mois']] },
    { icon: Percent, color: 'amber', title: `Parrainage d'ambassadeurs`,
      lines: [['10 %', 'De tous les gains des ambassadeurs que vous recrutez dans le programme'], ['Débloquer', `Recrutez 5 ambassadeurs pour activer votre palier d'équipe de 10 %`]] },
  ] : [
    { icon: UserPlus, color: 'blue', title: 'Service Provider Referrals',
      lines: [[`${cur}10`, 'Sign-up bonus when a referred provider completes their first job'], [`${cur}5`, `Per job completed by your referred provider (${cur}100+ subtotal; ${cur}2 under ${cur}100) — forever`]] },
    { icon: Users, color: 'purple', title: 'Customer Referrals',
      lines: [[`${cur}5`, `Per completed job booked by your referred customer (${cur}100+ subtotal; ${cur}2 under ${cur}100)`], ['No limit', 'The more active customers you refer, the more you earn each month']] },
    { icon: Percent, color: 'amber', title: 'Ambassador Referrals',
      lines: [['10%', 'Of all earnings made by ambassadors you recruit into the program'], ['Unlock', 'Recruit 5 ambassadors to activate your 10% team earnings tier']] },
  ];

  return (
    <div className="min-h-screen bg-white" data-testid="ambassador-city-page">
      <Helmet>
        <title>{title}</title>
        <meta name="description" content={metaDescription} />
        <link rel="canonical" href={canonical} />
        {isCA && <link rel="alternate" hrefLang="en" href={enUrl} />}
        {isCA && <link rel="alternate" hrefLang="fr-CA" href={frUrl} />}
        {isCA && <link rel="alternate" hrefLang="x-default" href={enUrl} />}
        <meta name="robots" content={city.thin ? 'noindex, follow' : 'index, follow'} />
        <meta property="og:title" content={title} />
        <meta property="og:description" content={metaDescription} />
        <meta property="og:url" content={canonical} />
        <meta property="og:type" content="website" />
        <meta property="og:image" content={OG_IMAGE} />
        <meta property="og:image:width" content="1264" />
        <meta property="og:image:height" content="848" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:image" content={OG_IMAGE} />
        <script type="application/ld+json">{JSON.stringify(buildJobPostingSchema(city))}</script>
        <script type="application/ld+json">{JSON.stringify(faqSchema)}</script>
        <script type="application/ld+json">{JSON.stringify(breadcrumbSchema)}</script>
      </Helmet>

      {/* Hero */}
      <header className="bg-gradient-to-br from-blue-700 via-indigo-700 to-purple-700 text-white">
        <div className="max-w-5xl mx-auto px-4 py-4">
          <button onClick={() => navigate('/ambassador')}
            className="inline-flex items-center gap-1 text-blue-200 text-sm hover:text-white transition"
            data-testid="ambassador-city-back-link">
            <ChevronLeft size={16} /> Ambassador Program
          </button>
        </div>
        <div className="max-w-5xl mx-auto px-4 pb-14 pt-6">
          <span className="inline-flex items-center gap-1.5 bg-yellow-400 text-gray-900 text-xs font-bold px-3 py-1.5 rounded-full mb-5">
            <Sparkles className="w-3.5 h-3.5" /> {fr ? `Occasion de revenus · ${city.name}, ${city.region_code}` : `Earning opportunity · ${city.name}, ${city.region_code}`}
          </span>
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-extrabold leading-tight mb-4">
            {fr ? `Devenez ambassadeur VanDeliveryApp à ${city.name}` : `Become a VanDeliveryApp Ambassador in ${city.name}`}
          </h1>
          <p className="text-base md:text-lg text-blue-100 max-w-2xl mb-8">
            {fr
              ? `Faites la promotion des services de déménagement et de livraison à ${city.name}, ${city.region} et gagnez une commission chaque fois qu'une réservation parrainée est payée et confirmée. Gratuit — sans véhicule, sans frais, sans horaire.`
              : `Promote moving & delivery services in ${city.name}, ${city.region} and earn a commission every time a booking you referred is paid and confirmed. Free to join — no vehicle, no fees, no schedule.`}
          </p>
          <div className="flex flex-col sm:flex-row gap-3">
            <Button onClick={() => navigate(registerHref)}
              className="bg-yellow-400 hover:bg-yellow-500 text-gray-900 px-8 py-6 rounded-xl text-lg font-bold shadow-xl"
              data-testid="ambassador-city-apply-btn">
              {fr ? `Commencez à gagner à ${city.name}` : `Start Earning in ${city.name}`} <ChevronRight className="w-5 h-5 ml-1" />
            </Button>
            <Button onClick={() => navigate('/ambassador/login')} variant="outline"
              className="bg-white/10 hover:bg-white/20 text-white border-2 border-white px-8 py-6 rounded-xl text-lg font-bold">
              {fr ? 'Connexion ambassadeur' : 'Ambassador Login'}
            </Button>
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mt-10">
            {(fr
              ? [[`${cur}10`, 'Prime fournisseur'], [`${cur}5`, `Par réservation ${cur}100+ (${cur}2 sous ${cur}100)`], ['10 %', `Palier d'équipe`], ['Mensuel', 'Virements Stripe Connect']]
              : [[`${cur}10`, 'Provider sign-up bonus'], [`${cur}5`, `Per ${cur}100+ booking (${cur}2 under ${cur}100)`], ['10%', 'Team earnings tier'], ['Monthly', 'Stripe Connect payouts']]
            ).map(([v, l]) => (
              <div key={l} className="bg-white/10 rounded-xl px-4 py-3">
                <p className="text-2xl font-extrabold">{v}</p>
                <p className="text-xs text-blue-100">{l}</p>
              </div>
            ))}
          </div>
        </div>
      </header>

      {/* Local intro */}
      <section className="max-w-4xl mx-auto px-4 py-14">
        <h2 className="text-lg font-bold text-gray-900 mb-4 flex items-center gap-2">
          <MapPin className="w-5 h-5 text-blue-600" /> {fr ? `Pourquoi ${city.name} est un excellent marché d'ambassadeur` : `Why ${city.name} is a great ambassador market`}
        </h2>
        {intro.map((p, i) => (
          <p key={i} className="text-base text-gray-600 leading-relaxed mb-4">{p}</p>
        ))}
      </section>

      {/* How it works */}
      <section className="bg-slate-50 py-14">
        <div className="max-w-5xl mx-auto px-4">
          <h2 className="text-lg font-bold text-gray-900 text-center mb-2">{fr ? `Comment ça marche à ${city.name}` : `How it works in ${city.name}`}</h2>
          <p className="text-center text-gray-500 mb-10 text-sm">{fr ? 'Simple, transparent et payant — commissions uniquement sur les réservations payées et confirmées' : 'Simple, transparent, and rewarding — commissions only on paid & confirmed bookings'}</p>
          <div className="grid sm:grid-cols-3 gap-6">
            {(fr ? [
              [Link2, 'Partagez votre lien', `Recevez un lien de parrainage unique et partagez-le avec des chauffeurs, déménageurs et clients partout à ${city.name}.`],
              [BarChart3, 'Suivez vos conversions', 'Suivez les clics, inscriptions et réservations complétées en temps réel depuis votre tableau de bord.'],
              [Wallet, 'Soyez payé chaque mois', `Dès qu'une réservation parrainée est payée et confirmée, votre commission est acquise. Virements mensuels via Stripe Connect.`],
            ] : [
              [Link2, 'Share your link', `Get a unique referral link and share it with drivers, movers, and customers across ${city.name} — online or in person.`],
              [BarChart3, 'Track conversions', 'Watch clicks, sign-ups, and completed bookings in real time from your ambassador dashboard.'],
              [Wallet, 'Get paid monthly', 'Once a referred booking is paid and confirmed, your commission is locked in. Payouts arrive monthly via Stripe Connect.'],
            ]).map(([Icon, h, d], i) => (
              <div key={h} className="bg-white rounded-2xl border border-slate-200 p-6 text-center">
                <div className="w-12 h-12 rounded-xl bg-blue-100 text-blue-700 flex items-center justify-center mx-auto mb-4"><Icon className="w-6 h-6" /></div>
                <h3 className="font-bold text-gray-900 mb-2">{i + 1}. {h}</h3>
                <p className="text-sm text-gray-600">{d}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Earning opportunities */}
      <section className="max-w-5xl mx-auto px-4 py-14">
        <h2 className="text-lg font-bold text-gray-900 text-center mb-10">{fr ? `Vos occasions de gains à ${city.name}` : `Your earning opportunities in ${city.name}`}</h2>
        <div className="grid sm:grid-cols-3 gap-6">
          {EARNING_CARDS.map(({ icon: Icon, color, title: ct, lines }) => (
            <div key={ct} className={`rounded-2xl border p-5 ${color === 'blue' ? 'border-blue-200 bg-blue-50/40' : color === 'purple' ? 'border-purple-200 bg-purple-50/40' : 'border-amber-200 bg-amber-50/40'}`}>
              <div className="flex items-center gap-2 mb-4">
                <Icon className={`w-5 h-5 ${color === 'blue' ? 'text-blue-600' : color === 'purple' ? 'text-purple-600' : 'text-amber-600'}`} />
                <h3 className="font-bold text-gray-900 text-sm">{ct}</h3>
              </div>
              {lines.map(([v, d]) => (
                <div key={d} className="bg-white rounded-lg p-3 mb-2 flex items-start justify-between gap-3">
                  <p className="text-xs text-gray-600">{d}</p>
                  <span className="font-extrabold text-gray-900 whitespace-nowrap">{v}</span>
                </div>
              ))}
            </div>
          ))}
        </div>

        {/* Income example */}
        <div className="mt-10 bg-green-50 border border-green-200 rounded-2xl p-6 sm:p-8">
          <h3 className="font-bold text-gray-900 text-center mb-1">{fr ? `Potentiel de revenu réel à ${city.name}` : `Real income potential in ${city.name}`}</h3>
          <p className="text-xs text-gray-500 text-center mb-6">{fr ? `Exemple avec un réseau actif modeste (travaux ${cur}100+) — vos gains dépendent de votre activité` : `Example with a modest active network (${cur}100+ subtotal jobs) — actual earnings depend on your referral activity`}</p>
          <div className="max-w-md mx-auto bg-white rounded-xl divide-y divide-slate-100 shadow-sm">
            {(fr ? [
              ['5 fournisseurs parrainés', `20 travaux/mois chacun × ${cur}5`, `${cur}500/mois`],
              ['10 clients parrainés', `2 travaux/mois chacun × ${cur}5`, `${cur}100/mois`],
              [`Primes d'inscription`, `5 nouveaux fournisseurs × ${cur}10`, `${cur}50`],
              ['5 ambassadeurs recrutés', `10 % de ${cur}1 000 de gains combinés`, `${cur}100/mois`],
            ] : [
              ['5 referred providers', `avg 20 jobs/month each × ${cur}5`, `${cur}500/mo`],
              ['10 referred customers', `2 jobs/month each × ${cur}5`, `${cur}100/mo`],
              ['Sign-up bonuses', `5 new providers × ${cur}10 first-job bonus`, `${cur}50`],
              ['5 recruited ambassadors', `10% of ${cur}1,000 combined earnings`, `${cur}100/mo`],
            ]).map(([a, b, c]) => (
              <div key={a} className="flex items-center justify-between px-5 py-3">
                <div><p className="text-sm font-semibold text-gray-800">{a}</p><p className="text-[11px] text-gray-500">{b}</p></div>
                <span className="font-bold text-green-700">{c}</span>
              </div>
            ))}
            <div className="flex items-center justify-between px-5 py-4 bg-green-100/60 rounded-b-xl">
              <p className="font-bold text-gray-900 text-sm">{fr ? 'Total mensuel' : 'Total monthly'}</p>
              <span className="text-2xl font-extrabold text-green-700">{cur}750+</span>
            </div>
          </div>
        </div>
      </section>

      {/* Where to promote (EN only — tips are written in English) */}
      {!fr && hoods.length > 0 && (
        <section className="bg-slate-50 py-14">
          <div className="max-w-5xl mx-auto px-4">
            <h2 className="text-lg font-bold text-gray-900 mb-2">Where to find referrals in {city.name}</h2>
            <p className="text-sm text-gray-500 mb-8">Neighborhood-by-neighborhood ideas for growing your referral network</p>
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {hoods.map((h, i) => (
                <div key={h} className="bg-white rounded-xl border border-slate-200 p-4">
                  <p className="font-semibold text-gray-900 text-sm mb-1 flex items-center gap-1.5"><MapPin className="w-3.5 h-3.5 text-blue-500" /> {h}</p>
                  <p className="text-xs text-gray-600">{buildHoodTip(h, city, i)}</p>
                </div>
              ))}
            </div>
            <p className="text-sm text-gray-600 mt-6">
              Customers you refer can book any service available in {city.name} — see{' '}
              <Link to={cityHref(city.slug)} className="text-blue-600 hover:underline font-medium">moving &amp; delivery services in {city.name}</Link>{' '}
              — and drivers you refer can apply via the{' '}
              <Link to={`/careers/moving-delivery/${city.slug}`} className="text-blue-600 hover:underline font-medium">{city.name} provider careers page</Link>.
            </p>
          </div>
        </section>
      )}

      {/* iter-366 — live local proof: top ambassadors */}
      {leaders.length > 0 && (
        <section className="max-w-4xl mx-auto px-4 pb-2">
          <div className="bg-amber-50 border border-amber-200 rounded-2xl p-6" data-testid="ambassador-city-leaderboard">
            <h2 className="text-lg font-bold text-gray-900 mb-1 flex items-center gap-2">
              <Trophy className="w-5 h-5 text-amber-500" /> {fr ? 'Nos meilleurs ambassadeurs ce mois-ci' : 'Top ambassadors this month'}
            </h2>
            <p className="text-xs text-gray-500 mb-4">{fr ? `De vraies personnes qui gagnent de vraies commissions — la prochaine place pourrait être la vôtre à ${city.name}.` : `Real people earning real commissions — the next spot could be yours in ${city.name}.`}</p>
            <div className="grid sm:grid-cols-5 grid-cols-2 gap-3">
              {leaders.slice(0, 5).map((l2, i) => (
                <div key={(l2.name || 'amb') + i} className="bg-white rounded-xl p-3 text-center border border-amber-100">
                  <p className="text-xl">{['🥇','🥈','🥉','🏅','🏅'][i]}</p>
                  <p className="text-xs font-bold text-gray-900 truncate">{l2.name || 'Ambassador'}</p>
                  {(l2.provider_conversions != null || l2.customer_conversions != null) && (
                    <p className="text-[11px] text-gray-500">{(l2.provider_conversions || 0) + (l2.customer_conversions || 0) + (l2.ambassador_conversions || 0)} {fr ? 'parrainages' : 'referrals'}</p>
                  )}
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* FAQs */}
      <section className="max-w-4xl mx-auto px-4 py-14">
        <h2 className="text-lg font-bold text-gray-900 mb-8">{fr ? `Questions fréquentes — ambassadeurs de ${city.name}` : `Frequently asked questions — ${city.name} ambassadors`}</h2>
        <div className="space-y-6">
          {faqs.map((f) => (
            <div key={f.q}>
              <h3 className="font-semibold text-gray-900 mb-1.5 flex items-start gap-2">
                <CheckCircle className="w-4 h-4 text-green-600 mt-1 flex-shrink-0" /> {f.q}
              </h3>
              <p className="text-sm text-gray-600 leading-relaxed pl-6">{f.a}</p>
            </div>
          ))}
        </div>
      </section>

      {/* CTA */}
      <section className="bg-gradient-to-r from-blue-700 to-purple-700 text-white py-14">
        <div className="max-w-3xl mx-auto px-4 text-center">
          <h2 className="text-lg md:text-lg font-bold mb-3">{fr ? `Prêt à gagner à ${city.name}?` : `Ready to start earning in ${city.name}?`}</h2>
          <p className="text-blue-100 mb-6 text-sm">{fr ? 'Inscrivez-vous gratuitement — partagez votre lien et soyez payé quand les réservations sont payées et confirmées.' : 'Join free today — share your link, and get paid when bookings are paid and confirmed.'}</p>
          <Button onClick={() => navigate(registerHref)}
            className="bg-yellow-400 hover:bg-yellow-500 text-gray-900 px-8 py-6 rounded-xl text-lg font-bold shadow-xl"
            data-testid="ambassador-city-cta-btn">
            {fr ? `Rejoindre le programme Ambassadeur` : 'Join the Ambassador Program'}
          </Button>
        </div>
      </section>

      {/* Nearby cities */}
      {nearby.length > 0 && (
        <section className="max-w-5xl mx-auto px-4 py-12">
          <h2 className="text-lg font-bold text-gray-900 mb-4">{fr ? `Occasions d'ambassadeur près de ${city.name}` : `Ambassador opportunities near ${city.name}`}</h2>
          <div className="flex flex-wrap gap-2" data-testid="ambassador-nearby-cities">
            {nearby.map((n) => (
              <Link key={n.slug} to={fr && (n.country_code || '').toUpperCase() === 'CA' ? `/fr/ambassadeur/${n.slug}` : `/ambassador/${n.slug}`}
                className="text-sm text-blue-700 bg-blue-50 hover:bg-blue-100 border border-blue-100 rounded-full px-3 py-1.5 transition">
                {n.name}, {n.region_code}
              </Link>
            ))}
          </div>
        </section>
      )}
    </div>
  );
}
